import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/pages/home/components/Logo";
import Footer from "@/pages/home/components/Footer";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CardBrands from "@/pages/checkout/components/CardBrands";
import { openCookieSettings } from "@/lib/cookieConsent";

export interface LegalSection {
  heading: string;
  body: string[];
  /** Show the accepted card logos under this section, e.g. on the payments terms. */
  cardBrands?: boolean;
  /** Cookies to list under this section, on the Cookie Policy. */
  cookies?: CookieEntry[];
  /** Show a button that reopens the cookie settings dialog. */
  cookieSettingsButton?: boolean;
}

export interface CookieEntry {
  name: string;
  type: string;
  purpose: string;
  expiry: string;
}

interface LegalPageProps {
  title: string;
  updated: string;
  sections: LegalSection[];
}

export default function LegalPage({ title, updated, sections }: LegalPageProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <header className="sticky top-0 z-50 bg-background-50/90 backdrop-blur-xl border-b border-background-200/70">
        <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-8">
          <Logo to="/" />
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            {/* Icon-only on phones, where the logo, switcher and label don't fit one row. */}
            <Link
              to="/"
              aria-label={t("legal.backToHome")}
              className="whitespace-nowrap rounded-full border border-background-200/70 px-3 sm:px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line sm:hidden" aria-hidden="true"></i>
              <span className="hidden sm:inline">{t("legal.backToHome")}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
          {t("legal.eyebrow")}
        </span>
        <h1 className="mt-3 text-3xl md:text-4xl font-bold text-foreground-950">{title}</h1>
        <p className="mt-3 text-sm text-foreground-500">{t("legal.lastUpdated", { date: updated })}</p>

        <div className="mt-10 space-y-10">
          {sections.map((s) => (
            <section key={s.heading}>
              <h2 className="text-xl font-bold text-foreground-950">{s.heading}</h2>
              {s.body.map((p, i) => (
                <p key={i} className="mt-3 text-sm leading-relaxed text-foreground-600">
                  {p}
                </p>
              ))}
              {s.cardBrands && <CardBrands className="mt-5" />}
              {s.cookies && (
                <div className="mt-5 space-y-3">
                  {s.cookies.map((c) => (
                    <dl key={c.name} className="rounded-xl border border-background-200/70 p-4 text-sm">
                      <dt className="font-mono font-semibold text-foreground-950 break-all">{c.name}</dt>
                      <dd className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-foreground-600">
                        <span className="text-foreground-500">{t("legal.cookieFields.type")}</span>
                        <span>{c.type}</span>
                        <span className="text-foreground-500">{t("legal.cookieFields.purpose")}</span>
                        <span>{c.purpose}</span>
                        <span className="text-foreground-500">{t("legal.cookieFields.expiry")}</span>
                        <span>{c.expiry}</span>
                      </dd>
                    </dl>
                  ))}
                </div>
              )}
              {s.cookieSettingsButton && (
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 cursor-pointer"
                >
                  <i className="ri-settings-3-line"></i>
                  {t("common.cookies.footerLink")}
                </button>
              )}
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}